/**
 * VRControllerHandler - Handles VR motion controllers
 *
 * Pure controller logic with no branching - knows exactly how to handle motion controllers.
 */

import { XRControllerModelFactory } from 'three/addons/webxr/XRControllerModelFactory.js';

import { VRDeviceHandler } from './VRDeviceHandler.js';

import type { World } from '../game/World.js';
import type * as THREE from 'three';

export class VRControllerHandler extends VRDeviceHandler {
  private controllerModelFactory: XRControllerModelFactory;
  private controllerModel: THREE.Object3D | null = null;

  constructor(
    inputSource: XRInputSource,
    renderer: THREE.WebGLRenderer,
    world: World,
    controllerIndex: number
  ) {
    super(inputSource, renderer, world, controllerIndex);
    this.controllerModelFactory = new XRControllerModelFactory();
    this.createVisualModel();
  }


  public getTargetRayGroup(): THREE.Group | null {
    if (!this.renderer?.xr.isPresenting) {
      return null;
    }
    return this.renderer.xr.getController(this.controllerIndex);
  }

  public getGripGroup(): THREE.Group | null {
    if (!this.renderer?.xr.isPresenting) {
      return null;
    }
    return this.renderer.xr.getControllerGrip(this.controllerIndex);
  }

  public createVisualModel(): void {
    if (!this.renderer) {
      return;
    }

    // Add controller groups to scene (important for ray visualization!)
    const controller = this.renderer.xr.getController(this.controllerIndex);
    const controllerGrip = this.renderer.xr.getControllerGrip(this.controllerIndex);

    this.world.scene.add(controller);
    this.world.scene.add(controllerGrip);

    // Create controller model
    this.controllerModel = this.controllerModelFactory.createControllerModel(controllerGrip);
    controllerGrip.add(this.controllerModel);

    console.log(`[VRControllerHandler] Created controller model for ${this.getHandedness()} hand`);
  }

  /** Get the controller model for visibility control */
  public getControllerModel(): THREE.Object3D | null {
    return this.controllerModel;
  }

  /** Set controller model visibility */
  public setControllerVisible(visible: boolean): void {
    if (this.controllerModel) {
      this.controllerModel.visible = visible;
    }
  }

  public dispose(): void {
    if (this.controllerModel && this.controllerModel.parent) {
      this.controllerModel.parent.remove(this.controllerModel);
      this.controllerModel = null;
    }

    console.log(`[VRControllerHandler] Disposed controller model for ${this.getHandedness()} hand`);
  }
}
